import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, decreaseQuantity, deleteFromCart } from "../../store/slices/cart-slice";
import { closeCartDrawer } from "../../store/slices/ui-slice";
import { useGetUserProfileQuery } from "../../store/slices/user-slice";
import { resolveImage, getLinePricing, formatMoney } from "../cart-redesign/pricing";

/*
 * Mirrors the stock ceiling addToCart enforces, purely so the "+" can be
 * disabled at the limit instead of silently warning. The reducer remains
 * the authority — this only affects the button's appearance.
 */
const atMax = (item) =>
  item.quantity >= (item.stock || 0) - (item.saleCount || 0);

/*
 * Slide-in cart panel.
 *
 * Opens itself whenever something is added to the cart (see ui-slice) and
 * shows what is now in the basket, so the confirmation is the cart itself
 * rather than only a toast. Pricing goes through the same getLinePricing
 * helper the cart and checkout pages use, so a line reads identically
 * wherever it appears.
 */
const CartDrawer = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  // The cart and checkout pages already show the basket in full, so a panel
  // sliding over them is only in the way.
  const suppressed = /\/(cart|checkout)\/?$/.test(pathname);
  const isCartDrawerOpen = useSelector((state) => state.ui.isCartDrawerOpen);
  const isOpen = isCartDrawerOpen && !suppressed;
  const currency = useSelector((state) => state.currency);
  const { cartItems } = useSelector((state) => state.cart);
  const { data } = useGetUserProfileQuery();
  const isReseller = data?.user?.role === "reseller";

  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const lastFocused = useRef(null);

  const close = () => dispatch(closeCartDrawer());

  // Reaching a suppressed route with the flag still set would leave the
  // drawer primed to spring open again on the way out, so clear it.
  useEffect(() => {
    if (suppressed && isCartDrawerOpen) dispatch(closeCartDrawer());
  }, [suppressed, isCartDrawerOpen, dispatch]);

  // Close on Escape, and keep Tab inside the panel while it is open.
  useEffect(() => {
    if (!isOpen) return undefined;

    lastFocused.current = document.activeElement;
    closeRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;

      const focusables = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Hand focus back to whatever opened the drawer.
      if (lastFocused.current instanceof HTMLElement) lastFocused.current.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Stop the page behind the drawer from scrolling, without letting the
  // scrollbar disappearing shift the layout.
  useEffect(() => {
    if (!isOpen) return undefined;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;
    const gap = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = "hidden";
    if (gap > 0) body.style.paddingRight = `${gap}px`;

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, [isOpen]);

  const count = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartItems.reduce((sum, item) => {
    const { unitPrice } = getLinePricing(item, currency, isReseller);
    return sum + unitPrice * item.quantity;
  }, 0);

  return (
    <div
      className={`c4cart-drawer${isOpen ? " is-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div className="c4cart-drawer__scrim" onClick={close} />

      <aside
        className="c4cart-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        ref={panelRef}
      >
        <header className="c4cart-drawer__head">
          <h2 className="c4cart-drawer__title">
            Your Cart
            {count > 0 ? (
              <span className="c4cart-drawer__count">{count}</span>
            ) : null}
          </h2>
          <button
            type="button"
            className="c4cart-drawer__close"
            onClick={close}
            aria-label="Close cart"
            ref={closeRef}
            tabIndex={isOpen ? 0 : -1}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        {cartItems.length === 0 ? (
          <div className="c4cart-drawer__empty">
            <svg
              width="46"
              height="46"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0" />
            </svg>
            <p>Your cart is empty.</p>
            <Link
              className="c4cart-drawer__shop"
              to={process.env.PUBLIC_URL + "/shop-grid-standard"}
              onClick={close}
              tabIndex={isOpen ? 0 : -1}
            >
              Start shopping
            </Link>
          </div>
        ) : (
          <>
            <ul className="c4cart-drawer__list">
              {cartItems.map((item) => {
                const { unitPrice, unitWas, discounted } = getLinePricing(
                  item,
                  currency,
                  isReseller
                );
                const link = `${process.env.PUBLIC_URL}/product/${item._id}`;

                return (
                  <li className="c4cart-drawer__item" key={item.cartItemId}>
                    <Link
                      className="c4cart-drawer__thumb"
                      to={link}
                      onClick={close}
                      tabIndex={isOpen ? 0 : -1}
                    >
                      {item.mainImage ? (
                        <img
                          src={resolveImage(item.mainImage)}
                          alt={item.name || "Product"}
                          loading="lazy"
                        />
                      ) : (
                        <span className="c4cart-drawer__noimg">No image</span>
                      )}
                    </Link>

                    <div className="c4cart-drawer__info">
                      <Link
                        className="c4cart-drawer__name"
                        to={link}
                        onClick={close}
                        title={item.name}
                        tabIndex={isOpen ? 0 : -1}
                      >
                        {item.name}
                      </Link>

                      {item.selectedVariation?.name ? (
                        <span className="c4cart-drawer__variation">
                          {item.selectedVariation.name}
                        </span>
                      ) : null}

                      <div className="c4cart-drawer__line">
                        <div className="c4cart-drawer__stepper">
                          <button
                            type="button"
                            onClick={() => dispatch(decreaseQuantity(item))}
                            aria-label={`Decrease quantity of ${item.name}`}
                            tabIndex={isOpen ? 0 : -1}
                          >
                            &minus;
                          </button>
                          <span aria-live="polite" aria-label="Quantity">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              dispatch(addToCart({ ...item, quantity: 1 }))
                            }
                            disabled={atMax(item)}
                            aria-label={`Increase quantity of ${item.name}`}
                            tabIndex={isOpen ? 0 : -1}
                          >
                            +
                          </button>
                        </div>
                        <span className="c4cart-drawer__price">
                          {formatMoney(currency, unitPrice * item.quantity)}
                          {discounted && unitWas != null ? (
                            <span className="c4cart-drawer__was">
                              {formatMoney(currency, unitWas * item.quantity)}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="c4cart-drawer__remove"
                      onClick={() => dispatch(deleteFromCart(item.cartItemId))}
                      aria-label={`Remove ${item.name} from cart`}
                      tabIndex={isOpen ? 0 : -1}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>

            <footer className="c4cart-drawer__foot">
              <div className="c4cart-drawer__subtotal">
                <span>Subtotal</span>
                <strong>{formatMoney(currency, subtotal)}</strong>
              </div>
              <p className="c4cart-drawer__note">
                Delivery and taxes are calculated at checkout.
              </p>
              <div className="c4cart-drawer__actions">
                <Link
                  className="c4cart-drawer__btn c4cart-drawer__btn--ghost"
                  to={process.env.PUBLIC_URL + "/cart"}
                  onClick={close}
                  tabIndex={isOpen ? 0 : -1}
                >
                  View cart
                </Link>
                <Link
                  className="c4cart-drawer__btn c4cart-drawer__btn--solid"
                  to={process.env.PUBLIC_URL + "/checkout"}
                  onClick={close}
                  tabIndex={isOpen ? 0 : -1}
                >
                  Checkout
                </Link>
              </div>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
